import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, MapPin, Clock, Navigation, ExternalLink, Cloud, CloudRain,
  CloudSnow, CloudFog, Sun, CloudSun, Wind, Droplets, Thermometer,
  Sparkles, Scissors, AlertTriangle, CalendarHeart, CloudLightning,
  Phone, MessageCircle, Heart, Briefcase,
} from "lucide-react";

/**
 * WeddingDayTab — assistant board for the day-of operations.
 *
 * Three discreet cards: Partenza intelligente · Meteo evento · AI Alert™.
 * Single endpoint round-trip (`GET /api/clients/:cid/wedding-day`) feeds
 * all three; the layout intentionally degrades gracefully (each card
 * renders its own "non disponibile" state) instead of swallowing the
 * whole tab when one upstream is unreachable.
 *
 * Design contract:
 *   - generous padding/whitespace, no charts, no tables
 *   - serif heading + champagne accents → matches the rest of the SaaS
 *   - touch-friendly: 56px min-height interactive zones for iPad use
 */

const WEATHER_ICON_MAP = {
  sun: Sun,
  "sun-cloud": CloudSun,
  cloud: Cloud,
  rain: CloudRain,
  drizzle: CloudRain,
  snow: CloudSnow,
  fog: CloudFog,
  storm: CloudLightning,
};

// Safari-safe HH:MM extractor.
//
// We deliberately avoid `new Date(<naive ISO>)` here: iOS Safari has a long
// history of returning `Invalid Date` (or NaN) for naive ISO 8601 strings
// like "2026-05-15T10:07:52" (no Z, no offset). When that happens, calling
// `.toLocaleTimeString()` on the result throws — and an uncaught throw in
// a render path on Safari iPad is exactly what triggers the
// "Si è verificato ripetutamente un errore" full-page crash.
//
// Backend always returns ISO-shaped strings, so a regex against `T(HH):(MM)`
// is reliable, locale-free, and cannot throw.
function formatHHMM(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const mIso = iso.match(/T(\d{2}):(\d{2})/);
  if (mIso) return `${mIso[1]}:${mIso[2]}`;
  const mAny = iso.match(/(\d{2}):(\d{2})/);
  return mAny ? `${mAny[1]}:${mAny[2]}` : "—";
}

function formatLongDate(ymd) {
  if (!ymd || typeof ymd !== "string") return "";
  try {
    const parts = ymd.split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
    const [y, m, d] = parts;
    // Constructing from numbers avoids Safari's naive-ISO parsing quirks.
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime())) return ymd;
    return date.toLocaleDateString("it-IT", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ymd; }
}

function safeNum(v) {
  // Coerces to a finite number or returns null. Avoids `Math.round(undefined)`
  // → NaN render artefacts and `${null}°` style ugliness.
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const TRAFFIC_LABELS = {
  scorrevole: { it: "Scorrevole", tone: "text-emerald-700" },
  moderato:   { it: "Moderato",   tone: "text-amber-700" },
  intenso:    { it: "Intenso",    tone: "text-rose-700" },
};

const REASON_HINTS = {
  no_starting_point: "Imposta il punto di partenza in Impostazioni per attivare il calcolo.",
  no_venue: "Aggiungi la location del matrimonio nella scheda Wedding.",
  no_prep_venue: "Aggiungi il luogo di preparazione (Wedding → Luogo preparazione) per calcolare la partenza.",
  no_work_start_time: "Imposta l'orario di inizio lavoro (Wedding → Orario inizio lavoro) per calcolare la partenza.",
  no_date: "Aggiungi la data del matrimonio per vedere le previsioni.",
  too_far: (d) => `Disponibile da circa ${Math.max(0, (d?.days_until ?? 0) - 14)} giorni prima del matrimonio.`,
  past: "Matrimonio già celebrato.",
  router_unreachable: "Servizio mappe temporaneamente non disponibile. Riprova più tardi.",
  weather_unreachable: "Servizio meteo temporaneamente non disponibile. Riprova più tardi.",
  missing_data: "Dati insufficienti per il calcolo.",
};

function reasonText(section) {
  const r = section?.reason;
  if (!r) return "";
  const v = REASON_HINTS[r];
  return typeof v === "function" ? v(section) : (v || "");
}

// ──────────────────────────────────────────────────────────────────────────
// Cards
// ──────────────────────────────────────────────────────────────────────────

function CardShell({ title, icon: Icon, children, testid }) {
  return (
    <div
      className="bg-white border border-border rounded-md p-6 sm:p-8"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-5">
        <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
        <h3 className="label-uppercase">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function TravelCard({ travel }) {
  if (!travel || !travel.available) {
    return (
      <CardShell title="Partenza intelligente" icon={Navigation} testid="wd-travel-card">
        <p className="text-sm text-neutral-500 italic" data-testid="wd-travel-unavailable">
          {reasonText(travel) || "Non disponibile."}
        </p>
      </CardShell>
    );
  }
  const t = TRAFFIC_LABELS[travel.traffic] || TRAFFIC_LABELS.moderato;
  const distanceKm = safeNum(travel.distance_km);
  const durationMin = safeNum(travel.duration_with_traffic_min);
  const destAddr = (travel.destination && travel.destination.address) || "—";
  return (
    <CardShell title="Partenza intelligente" icon={Navigation} testid="wd-travel-card">
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-xs text-neutral-500">Partenza consigliata</span>
        <span className="font-serif-heading text-5xl sm:text-6xl text-neutral-900 tabular-nums" data-testid="wd-travel-departure">
          {formatHHMM(travel.departure_time)}
        </span>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
        <div>
          <div className="label-uppercase text-neutral-500">Distanza</div>
          <div className="font-medium mt-1">{distanceKm != null ? `${distanceKm} km` : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase text-neutral-500">Percorrenza</div>
          <div className="font-medium mt-1">{durationMin != null ? `${durationMin} min` : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase text-neutral-500">Traffico</div>
          <div className={`font-medium mt-1 ${t.tone}`} data-testid="wd-travel-traffic">{t.it}</div>
        </div>
        <div>
          <div className="label-uppercase text-neutral-500">Arrivo previsto</div>
          <div className="font-medium mt-1 tabular-nums">{formatHHMM(travel.arrival_time)}</div>
        </div>
      </div>
      <div className="mt-6 pt-5 border-t border-border space-y-2 text-sm">
        {/* P0 fix Jun 2026 — Partenza Intelligente must show explicitly
            the three inputs of the computation so the operator can
            sanity-check at a glance (es. "Partenza da casa? sì. Arrivo a
            prep venue? sì. Orario riferimento = inizio lavoro? sì."). */}
        <div className="flex items-start gap-2 text-neutral-600">
          <span className="label-uppercase text-[10px] text-neutral-400 mt-0.5 w-28 shrink-0">Partenza da</span>
          <span className="text-neutral-800 truncate" data-testid="wd-travel-origin">
            {(travel.origin && travel.origin.address) || "Sede aziendale"}
          </span>
        </div>
        <div className="flex items-start gap-2 text-neutral-600">
          <span className="label-uppercase text-[10px] text-neutral-400 mt-0.5 w-28 shrink-0">Arrivo a</span>
          <span className="text-neutral-800 truncate" data-testid="wd-travel-destination">
            <MapPin className="inline w-3.5 h-3.5 text-primary mr-1 -mt-0.5" strokeWidth={1.5} />
            {destAddr}
            <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">
              Luogo preparazione
            </span>
          </span>
        </div>
        <div className="flex items-start gap-2 text-neutral-600">
          <span className="label-uppercase text-[10px] text-neutral-400 mt-0.5 w-28 shrink-0">Orario riferim.</span>
          <span className="text-neutral-800 tabular-nums" data-testid="wd-travel-reference">
            {formatHHMM(travel.arrival_time)} · <span className="text-neutral-500">{travel.arrival_label || "Inizio lavoro"}</span>
          </span>
        </div>
        <div className="pt-3 flex items-center gap-3 flex-wrap">
          {travel.provider === "google" ? (
            <span
              className="text-[11px] uppercase tracking-wider text-neutral-400"
              data-testid="wd-travel-provider"
              title="Calcolato con dati di traffico in tempo reale"
            >
              Traffico live
            </span>
          ) : null}
          {travel.maps_url ? (
            <a
              href={travel.maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors ml-auto"
              data-testid="wd-travel-maps-link"
            >
              Apri Maps
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
            </a>
          ) : null}
        </div>
      </div>
    </CardShell>
  );
}

function WeatherCard({ weather }) {
  if (!weather || !weather.available) {
    return (
      <CardShell title="Meteo evento" icon={Cloud} testid="wd-weather-card">
        <p className="text-sm text-neutral-500 italic" data-testid="wd-weather-unavailable">
          {reasonText(weather) || "Non disponibile."}
        </p>
      </CardShell>
    );
  }
  const Icon = WEATHER_ICON_MAP[weather.icon] || Cloud;
  const tEvent = safeNum(weather.temp_at_event ?? weather.temp_max);
  const humidity = safeNum(weather.humidity_at_event ?? weather.humidity_max);
  const wind = safeNum(weather.wind_at_event_kmh ?? weather.wind_max_kmh);
  const tMin = safeNum(weather.temp_min);
  const tMax = safeNum(weather.temp_max);
  return (
    <CardShell title="Meteo evento" icon={Cloud} testid="wd-weather-card">
      <div className="flex items-start gap-5 flex-wrap">
        <div className="flex items-center gap-4">
          <Icon className="w-12 h-12 text-primary shrink-0" strokeWidth={1.25} />
          <div>
            <div className="font-serif-heading text-5xl sm:text-6xl text-neutral-900 tabular-nums leading-none" data-testid="wd-weather-temp">
              {tEvent != null ? `${Math.round(tEvent)}°` : "—"}
            </div>
            <div className="text-sm text-neutral-600 mt-2" data-testid="wd-weather-condition">
              {weather.condition || "—"}
            </div>
          </div>
        </div>
        <div className="ml-auto grid grid-cols-3 gap-x-6 gap-y-3 text-sm pt-1">
          <div>
            <Droplets className="w-3.5 h-3.5 text-primary mb-1" strokeWidth={1.5} aria-hidden="true" />
            <div className="label-uppercase text-neutral-500">Umidità</div>
            <div className="font-medium mt-1">{humidity != null ? `${Math.round(humidity)}%` : "—"}</div>
          </div>
          <div>
            <Wind className="w-3.5 h-3.5 text-primary mb-1" strokeWidth={1.5} aria-hidden="true" />
            <div className="label-uppercase text-neutral-500">Vento</div>
            <div className="font-medium mt-1">{wind != null ? `${Math.round(wind)} km/h` : "—"}</div>
          </div>
          <div>
            <Thermometer className="w-3.5 h-3.5 text-primary mb-1" strokeWidth={1.5} aria-hidden="true" />
            <div className="label-uppercase text-neutral-500">Min / Max</div>
            <div className="font-medium mt-1 tabular-nums">
              {tMin != null ? `${Math.round(tMin)}°` : "—"}
              {" / "}
              {tMax != null ? `${Math.round(tMax)}°` : "—"}
            </div>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function AlertsCard({ alerts, weatherAvailable }) {
  const hairItems = Array.isArray(alerts?.hair) ? alerts.hair.filter((s) => typeof s === "string") : [];
  const makeupItems = Array.isArray(alerts?.makeup) ? alerts.makeup.filter((s) => typeof s === "string") : [];
  const empty = hairItems.length === 0 && makeupItems.length === 0;
  return (
    <CardShell title="AI Alert™" icon={Sparkles} testid="wd-alerts-card">
      {empty ? (
        <p className="text-sm text-neutral-500 italic" data-testid="wd-alerts-empty">
          {weatherAvailable
            ? "Condizioni nella media — nessun accorgimento speciale necessario."
            : "I suggerimenti saranno disponibili appena saranno pubblicate le previsioni meteo."}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-6">
          <AlertColumn title="Hair" icon={Scissors} items={hairItems} testid="wd-alerts-hair" />
          <AlertColumn title="Makeup" icon={Sparkles} items={makeupItems} testid="wd-alerts-makeup" />
        </div>
      )}
    </CardShell>
  );
}

function AlertColumn({ title, icon: Icon, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
        <span className="label-uppercase text-neutral-700">{title}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-neutral-400 italic">Nessun avviso.</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((tip, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-neutral-700 leading-relaxed">
              <AlertTriangle className="w-3.5 h-3.5 text-primary mt-1 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Tab root
// ──────────────────────────────────────────────────────────────────────────

export function WeddingDayTab({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!clientId) return undefined;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const r = await api.get(`/clients/${clientId}/wedding-day`);
        if (cancelled) return;
        // Defensive: only accept plain objects; anything else (string,
        // null, array — e.g. from a misrouted proxy) becomes a clean
        // empty state instead of crashing the render.
        const payload = r?.data;
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
          setData(payload);
        } else {
          setData({});
        }
      } catch (err) {
        if (cancelled) return;
        let msg = "";
        try { msg = formatApiError(err?.response?.data?.detail) || err?.message || ""; }
        catch { msg = "Errore di rete"; }
        setError(msg || "Impossibile caricare i dati del giorno del matrimonio.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="wedding-day-loading">
        <Loader2 className="w-6 h-6 text-primary animate-spin" strokeWidth={1.5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-md p-6 text-sm text-rose-700" data-testid="wedding-day-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="wedding-day-tab">
      {/* Discreet header — date + venue, the only contextual anchor */}
      {(data.wedding_date || data.venue) && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <CalendarHeart className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <span>
            {data.wedding_date ? formatLongDate(data.wedding_date) : ""}
            {data.wedding_date && data.venue ? " · " : ""}
            {data.venue || ""}
          </span>
        </div>
      )}

      {/* DAY TIMELINE — mobile-first key-moments card.
          Three chronological pillars of the day (work start, departure,
          ceremony). On mobile this is the FIRST thing the operator sees
          — large, glanceable, no scroll needed. On desktop it sits above
          the existing cards as a quick anchor without competing for
          attention. Renders only when at least one time is known. */}
      <DayTimelineCard data={data} />

      <TravelCard travel={data.travel} />
      <WeatherCard weather={data.weather} />
      <AlertsCard alerts={data.alerts} weatherAvailable={!!data.weather?.available} />

      {/* STICKY CTA BAR (mobile only).
          Sits above the OS home indicator using safe-area-inset-bottom
          padding. 3 buttons of equal size, 56px tall (well above the
          Apple HIG 44pt minimum), accessible with the thumb of any hand
          on phones up to 6.7". Disabled state when phone or venue
          missing — never hides, so the operator always knows where to
          look. */}
      <DayOfActionBar
        clientName={data.client_name}
        clientPhone={data.client_phone}
        venue={data.venue}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Mobile-first helpers (Feb 2026 mobile audit)
// ──────────────────────────────────────────────────────────────────────────

// Strip everything except + and digits so phone deep-links work on iOS
// and Android. Kept in this module instead of a shared helper because
// Events.jsx has its own copy too and we want zero cross-module
// coupling between feature pages.
function _cleanPhone(raw) {
  if (!raw) return "";
  return String(raw).replace(/[^\d+]/g, "");
}
function _telHref(raw) {
  const p = _cleanPhone(raw);
  return p ? `tel:${p}` : "";
}
function _whatsappHref(raw, msg) {
  const p = _cleanPhone(raw).replace(/^\+/, "");
  if (!p) return "";
  return msg
    ? `https://wa.me/${p}?text=${encodeURIComponent(msg)}`
    : `https://wa.me/${p}`;
}
function _mapsHref(loc) {
  if (!loc) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc)}`;
}

// DayTimelineCard — three key moments stacked vertically.
//
// On mobile these are LARGE so they can be read at a glance during
// preparation. On desktop they share a horizontal layout to mirror the
// other cards. We never invent missing data: if a time is absent we
// render an "—" placeholder so the operator immediately spots gaps in
// the briefing.
function DayTimelineCard({ data }) {
  const workStart = data?.work_start_time || null;
  const departure = data?.travel?.available && data.travel.departure_time
    ? _hhmm(data.travel.departure_time)
    : null;
  const ceremony = data?.wedding_time || null;

  // Don't render the card if all three are missing — the legacy cards
  // already convey "missing data" via their own empty states.
  if (!workStart && !departure && !ceremony) return null;

  return (
    <CardShell title="Tappe del giorno" icon={Clock} testid="wd-timeline-card">
      <div className="grid grid-cols-3 gap-2 md:gap-6">
        <TimelineMoment
          icon={Briefcase}
          label="Inizio lavoro"
          time={workStart}
          tone="text-neutral-700"
          testid="wd-moment-work-start"
        />
        <TimelineMoment
          icon={Navigation}
          label="Partenza"
          time={departure}
          tone="text-amber-700"
          testid="wd-moment-departure"
        />
        <TimelineMoment
          icon={Heart}
          label="Cerimonia"
          time={ceremony}
          tone="text-rose-700"
          testid="wd-moment-ceremony"
        />
      </div>
    </CardShell>
  );
}

function TimelineMoment({ icon: Icon, label, time, tone, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <Icon className={`w-5 h-5 mx-auto ${tone}`} strokeWidth={1.5} aria-hidden="true" />
      <div className="label-uppercase text-neutral-500 mt-2 text-[10px]">{label}</div>
      <div className={`font-serif-heading text-2xl md:text-3xl mt-1 tabular-nums ${time ? "text-neutral-900" : "text-neutral-300"}`}>
        {time || "—"}
      </div>
    </div>
  );
}

function _hhmm(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : null;
}

// DayOfActionBar — sticky bottom bar on mobile, inline footer on desktop.
//
// Three full-width buttons: Chiama · WhatsApp · Maps. We deliberately
// keep this OUT of the WeddingDayTab body flow on desktop so the user's
// "do not change desktop" constraint is honoured: `md:hidden` ensures
// the bar only manifests on small viewports. Padding accounts for the
// iOS home indicator via env(safe-area-inset-bottom).
function DayOfActionBar({ clientName, clientPhone, venue }) {
  const tel = _telHref(clientPhone);
  const wa = _whatsappHref(
    clientPhone,
    clientName ? `Ciao ${clientName}, ti scrivo per il tuo matrimonio.` : "",
  );
  const maps = _mapsHref(venue);

  // Hide entirely on desktop — desktop already has the "Apri Maps" link
  // inside the TravelCard, and the user explicitly wanted the desktop
  // experience preserved.
  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-30 bg-white/95 backdrop-blur border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="wd-action-bar"
    >
      <div className="grid grid-cols-3 gap-2 p-3">
        <a
          href={tel || undefined}
          onClick={(e) => { if (!tel) e.preventDefault(); }}
          aria-disabled={!tel}
          className={`flex flex-col items-center justify-center gap-0.5 h-14 rounded-md border transition-colors ${
            tel
              ? "bg-emerald-50 border-emerald-200 text-emerald-800 active:bg-emerald-100"
              : "bg-neutral-50 border-neutral-200 text-neutral-300"
          }`}
          data-testid="wd-action-call"
        >
          <Phone className="w-5 h-5" strokeWidth={1.7} />
          <span className="text-[10.5px] font-medium uppercase tracking-wide">Chiama</span>
        </a>
        <a
          href={wa || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { if (!wa) e.preventDefault(); }}
          aria-disabled={!wa}
          className={`flex flex-col items-center justify-center gap-0.5 h-14 rounded-md border transition-colors ${
            wa
              ? "bg-green-50 border-green-200 text-green-800 active:bg-green-100"
              : "bg-neutral-50 border-neutral-200 text-neutral-300"
          }`}
          data-testid="wd-action-whatsapp"
        >
          <MessageCircle className="w-5 h-5" strokeWidth={1.7} />
          <span className="text-[10.5px] font-medium uppercase tracking-wide">WhatsApp</span>
        </a>
        <a
          href={maps || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { if (!maps) e.preventDefault(); }}
          aria-disabled={!maps}
          className={`flex flex-col items-center justify-center gap-0.5 h-14 rounded-md border transition-colors ${
            maps
              ? "bg-blue-50 border-blue-200 text-blue-800 active:bg-blue-100"
              : "bg-neutral-50 border-neutral-200 text-neutral-300"
          }`}
          data-testid="wd-action-maps"
        >
          <Navigation className="w-5 h-5" strokeWidth={1.7} />
          <span className="text-[10.5px] font-medium uppercase tracking-wide">Maps</span>
        </a>
      </div>
    </div>
  );
}

export default WeddingDayTab;
